import { z } from 'zod'
import { ApiError } from '@/util/errors/api-error'

export enum EditingResourceKind {
  MED_CHRON = 'med_chron',
  TIMELINE = 'timeline',
}

export enum EditingLeaseState {
  AVAILABLE = 'available',
  HELD = 'held',
}

export enum EditingLeaseErrorCode {
  CONFLICT = 'editing_lease_conflict',
  DISABLED = 'editing_lease_disabled',
}

export const EditingLeaseReadSchema = z.object({
  resource: z.object({ kind: z.nativeEnum(EditingResourceKind), resource_id: z.string().uuid(), matter_id: z.string().uuid() }),
  server_now: z.string().datetime({ offset: true }),
  expires_at: z.string().datetime({ offset: true }).nullable(),
  state: z.nativeEnum(EditingLeaseState),
  holder_display_name: z.string().nullable(),
  is_current_user_holder: z.boolean(),
  heartbeat_interval_seconds: z.number().positive(),
})
export type EditingLeaseRead = z.infer<typeof EditingLeaseReadSchema>

export const EditingLeaseErrorDetailSchema = z.object({
  code: z.nativeEnum(EditingLeaseErrorCode),
  lease: EditingLeaseReadSchema,
})
export type EditingLeaseErrorDetail = z.infer<typeof EditingLeaseErrorDetailSchema>

/** Classify a validated response without parsing or normalizing its detail again. */
export function isEditingLeaseDenial(
  status: number,
  parsed: z.SafeParseReturnType<unknown, EditingLeaseErrorDetail>
): parsed is z.SafeParseSuccess<EditingLeaseErrorDetail> {
  return status === 409 && parsed.success
}

/** Preserve typed lease denials while leaving other failures to the endpoint's error handling. */
export function throwIfEditingLeaseDenial(status: number, detail: unknown): void {
  const parsed = EditingLeaseErrorDetailSchema.safeParse(detail)
  if (isEditingLeaseDenial(status, parsed)) {
    throw new ApiError(status, parsed.data.code, parsed.data)
  }
}

export function getEditingLeaseError(error: unknown): EditingLeaseErrorDetail | null {
  if (!(error instanceof ApiError)) return null
  const parsed = EditingLeaseErrorDetailSchema.safeParse(error.detail)
  return isEditingLeaseDenial(error.status, parsed) ? parsed.data : null
}
